import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { Button } from '@lsg/components';

import InteractiveMap from '../components/interactive-map/InteractiveMap';
import LocationInfoCards, {
    LocationInfoCard,
} from '../components/locations/LocationInfoCards';
import { locationInfoCardsData } from '../components/locations/locationInfoCardsData';
import EventSchedule from '../components/event-schedule/EventSchedule';
import Headline from '../components/common/Headline';
import CustomCarousel from '../components/custom-carousel/CustomCarousel';
import LocationAddress from '../components/locations/LocationAddress';
import FewWavesManyPeaks from '../components/background-transitions/FewWavesManyPeaks';
import FewWavesLessPeaks from '../components/background-transitions/FewWavesLessPeaks';
import {
    getSelectedCountry,
    getSelectedLocation,
} from '../utils/locationUtils';
import { handleScrollById } from '../utils/windowUtils';

import { eventsData } from '../components/event-schedule/eventScheduleData';
import FrankfurtBanner from '../assets/images/locations/banners/collabothon_frankfurt_banner.png';
import SofiaBanner from '../assets/images/locations/banners/collabothon_sofia_banner.png';
import LodzBanner from '../assets/images/locations/banners/collabothon_lodz_banner.png';
import PragueBanner from '../assets/images/locations/banners/collabothon_prague_banner.png';

const LocationsPage = () => {
    let { locationId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const schedule = useRef(null);
    const address = useRef(null);

    const [selectedCountry, setSelectedCountry] = useState('');

    const executeScroll = (anchor) => {
        setTimeout(() => {
            switch (anchor) {
                case 'address': {
                    address.current.scrollIntoView();
                    break;
                }
                case 'schedule': {
                    schedule.current.scrollIntoView();
                    break;
                }
                default: {
                    console.log('Anchor not found');
                }
            }
        }, 50);
    };

    // filter the location data for the selected country
    const cardsData = locationInfoCardsData.find(
        (element) => element.country === selectedCountry
    );

    // filter the location events for the selected country
    const events = eventsData.find(
        (events) => events.country === selectedCountry
    )?.schedule;

    const getBannerImage = (selectedLocation) => {
        switch (selectedLocation) {
            case 'Frankfurt':
                return FrankfurtBanner;
            case 'Prague':
                return PragueBanner;
            case 'Łódź':
                return LodzBanner;
            case 'Sofia':
                return SofiaBanner;
            default:
                return 'none';
        }
    };

    useEffect(() => {
        getSelectedCountry(locationId) &&
            setSelectedCountry(
                String(getSelectedCountry(locationId)).charAt(0).toUpperCase() +
                    String(getSelectedCountry(locationId)).slice(1)
            );

        location.hash && executeScroll(location.hash.slice(1));

        if (selectedCountry) {
            handleScrollById('locationInfoCards', isMobile ? 80 : 50);
        }
    }, [selectedCountry]);

    return (
        <>
            <div
                className={`${
                    selectedCountry === '' ? 'mb-12' : '-mb-8'
                } pt-8 md:pt-24 `}
            >
                <div className="container m-auto">
                    <Headline
                        headline={'Select your Location of Interest'}
                        classNames="text-primary2"
                    />
                    <InteractiveMap
                        selectedCountry={selectedCountry}
                        setSelectedCountry={(country) => {
                            navigate(
                                `/locations/${getSelectedLocation(
                                    country
                                ).toLocaleLowerCase()}`
                            );
                            setSelectedCountry(country);
                        }}
                    />
                </div>
            </div>

            {selectedCountry && (
                <>
                    {/* Cards */}
                    {isMobile ? (
                        <FewWavesLessPeaks
                            bgColor={'#fff'}
                            waveColor={'#bfcbce'}
                        />
                    ) : (
                        <FewWavesManyPeaks
                            bgColor={'#fff'}
                            waveColor={'#bfcbce'}
                        />
                    )}
                    <div className="bg-primary2/25 pb-8 sm:pb-16">
                        <div
                            className="sm:pt-16"
                            id={'locationInfoCards'}
                        >
                            <Headline
                                headline={getSelectedLocation(selectedCountry)}
                                classNames="text-primary2"
                            />
                        </div>

                        {/* TODO: carousel is not consistent with carousel in challenges (touches edge of screen) - which is better? */}
                        {isMobile ? (
                            <div className={'lg:hidden w-[100%] md:w-[90%]'}>
                                <CustomCarousel>
                                    {cardsData.info.map((item, index) => (
                                        <LocationInfoCard
                                            locationInfo={item}
                                            key={index}
                                        />
                                    ))}
                                </CustomCarousel>
                            </div>
                        ) : (
                            <div className="max-w-screen-2xl mx-auto ">
                                <LocationInfoCards data={cardsData.info} />
                            </div>
                        )}
                    </div>

                    {isMobile ? (
                        <FewWavesLessPeaks
                            bgColor={'#bfcbce'}
                            waveColor={'#002e3c'}
                        />
                    ) : (
                        <FewWavesManyPeaks
                            bgColor={'#bfcbce'}
                            waveColor={'#002e3c'}
                        />
                    )}

                    {/* Schedule */}
                    <div
                        className="bg-primary2"
                        ref={schedule}
                    >
                        <div
                            className="max-w-screen-2xl mx-auto sm:pt-16 pb-8 sm:pb-16 px-4"
                            id="eventSchedule"
                        >
                            <EventSchedule data={events} />
                        </div>
                    </div>

                    {isMobile ? (
                        <FewWavesLessPeaks
                            bgColor={'#002e3c'}
                            waveColor={'#ffffff'}
                        />
                    ) : (
                        <FewWavesManyPeaks
                            bgColor={'#002e3c'}
                            waveColor={'#ffffff'}
                        />
                    )}

                    {/* Address */}
                    <div
                        className="max-w-screen-2xl mx-auto py-16"
                        id="venueAdress"
                        ref={address}
                    >
                        <Headline
                            headline={`Address of the Venue`}
                            classNames="text-primary2"
                        />
                        <div className="flex justify-center items-center px-8 sm:px-12">
                            <LocationAddress
                                selectedLocation={getSelectedLocation(
                                    selectedCountry
                                )}
                            />
                        </div>
                    </div>

                    <div className="relative">
                        <div className="flex justify-center items-center px-8 sm:px-12 mb-24">
                            <div className="relative">
                                <img
                                    className="w-full object-cover h-[512px] object-center rounded-xl"
                                    src={getBannerImage(
                                        getSelectedLocation(selectedCountry)
                                    )}
                                    alt={'Pick your challenge banner'}
                                />
                            </div>
                        </div>
                        <div className="absolute bottom-[26px] m-auto left-0 right-0">
                            <div className="flex justify-center items-center mx-24 sm:mx-0">
                                <Button
                                    href={'/challenges'}
                                    className={'m-0'}
                                >
                                    Pick Your Challenge
                                </Button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default LocationsPage;
